import { useRef } from 'react'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useMutation } from 'react-query'
import { useTranslation } from 'next-i18next'

// Libs
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { setUserOffByHostOut, setHostDisconnectUser, removeMicOnItem, setRoomHandUpClose } from '@/redux/reducers/socket'
import { setLiveType } from '@/redux/reducers/room'
import { setLiveRoomOff } from '@/api/setLiveRoomOff'
import useLiveTime from '@/hooks/useLiveTime'
import getSearchParams from '@/methods/url/getSearchParams'
import { handleGtmLiveInteraction } from '@/methods/handleGtmEvent'
import { getRoomHandUpList } from '@/methods/message/getRoomHandUpList'

// Components
import Popup from '@/components/common/Popup'
import ActionIcon from '@/components/common/ActionIcon'
import Icon from '@/assets/icons'
import Tooltip, { Place } from '@/components/common/Tooltip'

// Constants
import { ROOM_LIVE_TYPE, LIVE_SOURCE_TYPE } from '@/constants/liveRoom'
import { ROLE } from '@/constants/role'
import { GtmLiveInteractionType } from '@/constants/gtmEvent'

// Types

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

interface PhoneActionProps {
  isHost: boolean
  userToken: string
  role: number
  isMicConnecting: boolean
  isWebRTCConnecting: boolean
  isMicEnabled: boolean
  isCameraEnabled: boolean
  isDisabledCameraSwitch: boolean
  isMultiCamera: boolean
  handleMicStatus: () => void
  handleCameraStatus: () => void
  handleCameraSwitch: () => void
  canHandUp: boolean
  socket: any
}

const PhoneAction = (props: PhoneActionProps) => {
  const {
    isHost,
    userToken,
    role,
    isMicConnecting,
    isWebRTCConnecting,
    isMicEnabled,
    isCameraEnabled,
    isDisabledCameraSwitch,
    isMultiCamera,
    handleMicStatus,
    handleCameraStatus,
    handleCameraSwitch,
    canHandUp,
    socket,
  } = props

  const { t } = useTranslation()

  const roomClosePopupRef = useRef<any>(null)

  const roomData = useAppSelector((state) => state.room.roomData)
  const isMP4 = roomData?.liveSourceType === LIVE_SOURCE_TYPE.MP4

  const isIt = role === ROLE.IT

  const { endTime = '' } = roomData

  const dispatch = useAppDispatch()

  const router = useRouter()
  const { query } = router
  const { roomId = '' } = query

  const searchParams = getSearchParams()

  const setLiveRoomOffMutation = useMutation(setLiveRoomOff)

  const { endCountTime, isLessThenTenMinutes } = useLiveTime({ startTime: new Date().getTime(), endTime: endTime, isOnlyEndCount: true })

  const handlePhoneEndClick = () => {
    if (isHost) {
      dispatch(setHostDisconnectUser({ uid: userToken }))
    } else {
      getRoomHandUpList({ socket })
      dispatch(setUserOffByHostOut())
    }
    handleGtmLiveInteraction({
      interactionType: GtmLiveInteractionType.LIVE_DISCONNECT,
      roomId: roomId as string,
      hostId: roomData.memberInfoId,
    })

    dispatch(removeMicOnItem({ uid: userToken }))
  }

  const handleRoomClose = () => {
    if (canHandUp) {
      dispatch(setRoomHandUpClose({ canHandUp: false }))

      if (isWebRTCConnecting) dispatch(setHostDisconnectUser({ uid: userToken }))
    }

    setLiveRoomOffMutation.mutateAsync({ roomId, test_mode: searchParams?.testMode || null }).then(() => {
      dispatch(setLiveType({ liveType: ROOM_LIVE_TYPE.PAST }))
    })

    roomClosePopupRef.current.setIsPopupOpen(false)
  }

  return (
    <div className={cx('phoneAction', { isMP4 })}>
      {isHost && (
        <div id='tool' className={cx('phoneAction-icon')} onClick={() => roomClosePopupRef.current.setIsPopupOpen(true)}>
          <div className={cx('phoneAction-icon-halo', { isCounting: isLessThenTenMinutes, isMP4 })} />
          <ActionIcon className={cx('phoneAction-icon-end', { isCounting: isLessThenTenMinutes })} icon={Icon.PhoneClose} />

          <Tooltip id='tool' place={Place.LEFT} isOpen={isLessThenTenMinutes} offset={14}>
            <div className={cx('phoneAction-end')}>
              <p className={cx('phoneAction-end-time')}>{endCountTime}</p>
              <p className={cx('phoneAction-end-text')}>{t('LiveRoom_End')}</p>
            </div>
          </Tooltip>
        </div>
      )}

      {!isIt && !isMP4 && (
        <div id='phoneActionWrapper' className={cx('phoneAction-wrapper')}>
          <div className={cx('phoneAction-icon')} onClick={handleMicStatus}>
            <ActionIcon icon={isMicEnabled ? Icon.Mic : Icon.MicOff} />
          </div>
          <div className={cx('phoneAction-icon')} onClick={handleCameraStatus}>
            <ActionIcon icon={isCameraEnabled ? Icon.Camera : Icon.CameraOff} />
          </div>
          {isMultiCamera && (
            <div className={cx('phoneAction-icon')} onClick={handleCameraSwitch}>
              <ActionIcon icon={Icon.Switch} isDisabled={isDisabledCameraSwitch} />
            </div>
          )}

          {isMicConnecting && (
            <div className={cx('phoneAction-icon')} onClick={handlePhoneEndClick}>
              <ActionIcon icon={Icon.HangUp} />
            </div>
          )}
        </div>
      )}

      <Popup
        ref={roomClosePopupRef}
        title={t('LiveRoom_RoomClosePopup_Title')}
        confirmLabel={t('LiveRoom_RoomClosePopup_Cancel')}
        onConfirm={handleRoomClose}
        cancelLabel={t('Common_Cancel')}
        onCancel={() => roomClosePopupRef.current.setIsPopupOpen(false)}
      />
    </div>
  )
}

export default PhoneAction
